<template>
    <el-dialog title="定制需求详情" :visible.sync="visible" width="900px" append-to-body @closed="onClosed">
        <div v-if="detail">
            <!-- 需求主信息（接口返回平铺对象） -->
            <el-descriptions :column="1" border>
                <el-descriptions-item label="需求编号">{{ d.demandNo || '-' }}</el-descriptions-item>
                <el-descriptions-item label="定制软件标题">{{ d.title || '-' }}</el-descriptions-item>
                <el-descriptions-item label="定制模式">{{ modeText(d.mode) }}</el-descriptions-item>
                <el-descriptions-item label="当前状态">
                    <el-tag :type="demandStatusType" size="small">{{ demandStatusText }}</el-tag>
                    <el-button v-if="demandStatus === 5 && isOwner" style="margin-left: 5px;" type="primary" size="mini"
                        @click="confirmAccept">确认验收</el-button>
                </el-descriptions-item>
                <el-descriptions-item v-if="isCrowdfunding" label="几人众筹">{{ d.crowdNumber || 0 }}
                    人</el-descriptions-item>
                <el-descriptions-item v-if="isCrowdfunding" label="已参与人数">{{ d.joinedCount || 0 }}
                    人</el-descriptions-item>
                <el-descriptions-item v-if="isCrowdfunding" label="已筹金额">{{ formatBudget(d.raisedAmount)
                }}</el-descriptions-item>
                <el-descriptions-item v-if="isCrowdfunding" label="众筹状态">{{ crowdStatusText(d.crowdStatus)
                }}</el-descriptions-item>
                <el-descriptions-item label="需求详细说明">{{ d.demand || '-' }}</el-descriptions-item>
                <el-descriptions-item label="预算金额">{{ formatBudget(d.budget) }}</el-descriptions-item>
                <el-descriptions-item label="预算补充说明">{{ d.budgetRemark || '-' }}</el-descriptions-item>
                <el-descriptions-item v-if="d.periodDays != null && d.periodDays !== ''" label="交付周期">{{ d.periodDays }}
                    天</el-descriptions-item>
                <el-descriptions-item label="需求附件">
                    <template v-if="fileList(d.demandFiles || d.demandFile || d.attachments).length">
                        <div v-for="(f, i) in fileList(d.demandFiles || d.demandFile || d.attachments)" :key="i"
                            class="file-item">
                            <a :href="fileUrl(f)" target="_blank" class="file-link">{{ fileName(f) }}</a>
                        </div>
                    </template>
                    <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item v-if="d.escrowAmount != null && d.escrowAmount !== ''" label="托管金额">{{
                    formatBudget(d.escrowAmount) }}</el-descriptions-item>
                <el-descriptions-item v-if="d.escrowStatus != null && d.escrowStatus !== ''" label="托管状态">{{
                    escrowStatusText(d.escrowStatus) }}</el-descriptions-item>
                <el-descriptions-item label="创建时间">{{ d.createTime || '-' }}</el-descriptions-item>
                <el-descriptions-item label="更新时间">{{ d.updateTime || '-' }}</el-descriptions-item>
            </el-descriptions>

            <!-- 投标方案列表（来自 detail.bids） -->
            <div class="bid-section">
                <div class="bid-title">
                    <span>投标方案列表</span>
                    <el-button type="text" size="mini" icon="el-icon-refresh" :loading="bidRefreshing"
                        @click="refreshBids">刷新</el-button>
                </div>
                <el-table :data="bidList" style="width: 100%" empty-text="暂无投标方案" border
                    :cell-style="{ 'text-align': 'center' }"
                    :header-cell-style="{ background: '#DBDBDB', color: '#000', 'text-align': 'center' }">
                    <el-table-column label="投标方" min-width="120" show-overflow-tooltip>
                        <template slot-scope="{ row }">{{ row.bid && (row.bid.userName || '-') }}</template>
                    </el-table-column>
                    <el-table-column label="投标金额" width="120">
                        <template slot-scope="{ row }">{{ formatBudget(row.bid && row.bid.price != null ? row.bid.price
                            : (row.bid && row.bid.amount != null ? row.bid.amount : (row.price != null ? row.price :
                                row.amount))) }}</template>
                    </el-table-column>
                    <el-table-column label="团队/个人" min-width="120" show-overflow-tooltip>
                        <template slot-scope="{ row }">{{ row.bid && (row.bid.teamName || '-') }}</template>
                    </el-table-column>
                    <el-table-column label="联系电话" width="130" show-overflow-tooltip>
                        <template slot-scope="{ row }">{{ (row.bid && row.bid.contactPhone != null &&
                            row.bid.contactPhone !== '') ? row.bid.contactPhone : (row.contactPhone != null &&
                                row.contactPhone !== '' ? row.contactPhone : '-') }}</template>
                    </el-table-column>
                    <el-table-column label="说明" min-width="140" show-overflow-tooltip>
                        <template slot-scope="{ row }">{{ (row.bid && (row.bid.solution != null &&
                            row.bid.solution !== '' ? row.bid.solution : (row.solution || '-'))) || '-'
                        }}</template>
                    </el-table-column>
                    <el-table-column label="投标时间" width="160">
                        <template slot-scope="{ row }">{{ row.createTime || (row.bid && row.bid.createTime) || '-'
                            }}</template>
                    </el-table-column>
                    <el-table-column label="操作" width="120" fixed="right">
                        <template slot-scope="{ row }">
                            <template v-if="isBidSelected(row)">
                                <el-tag type="success" size="mini">已选标</el-tag>
                                <el-button v-if="canCancelSelect" type="text" size="mini" style="color:#F56C6C"
                                    @click="cancelSelectBid(row)">撤标</el-button>
                            </template>
                            <el-button v-else-if="!hasSelectedBid" type="text" size="mini" style="color:#E6A23C"
                                @click="selectBid(row)">选标</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 选标后提交投标方案（已选标待签合同且尚未上传合同） -->
                <div class="bid-submit-bar" v-if="canUploadContract">
                    <el-button type="primary" size="small" @click="openSubmit">上传合同文件</el-button>
                    <span class="bid-submit-tip">已选标，可上传合同文件；如需改选其他投标方，请先点击上方「撤标」撤销当前选标</span>
                </div>
                <!-- 已签合同待托管项目款：打款 -->
                <div class="bid-submit-bar" v-if="canPay">
                    <el-button type="success" size="small" @click="openPay">打款</el-button>
                    <span class="bid-submit-tip">需求已进入「已签合同待托管项目款」阶段</span>
                </div>
            </div>

            <!-- 托管/追加资金记录（来自 detail.escrowRecords） -->
            <div class="escrow-section" v-if="escrowList.length">
                <div class="escrow-title">
                    <span>托管 / 追加资金记录</span>
                    <el-tag size="mini" type="info">{{ escrowList.length }} 条</el-tag>
                </div>
                <el-table :data="escrowList" style="width: 100%" border :cell-style="{ 'text-align': 'center' }"
                    :header-cell-style="{ background: '#DBDBDB', color: '#000', 'text-align': 'center' }">
                    <el-table-column label="订单号" width="180">
                        <template slot-scope="{ row }">{{ row.orderNo }}</template>
                    </el-table-column>
                    <el-table-column label="资金类型" width="100">
                        <template slot-scope="{ row }">{{ escrowProjectTypeText(row.projectType) }}</template>
                    </el-table-column>
                    <el-table-column prop="projectAmount" label="金额" width="110">
                        <template slot-scope="{ row }">{{ formatBudget(row.projectAmount) }}</template>
                    </el-table-column>
                    <el-table-column prop="status" label="支付状态" width="170">
                        <template slot-scope="{ row }">{{ escrowPayStatusText(row.status) }}</template>
                    </el-table-column>
                    <el-table-column prop="createTime" label="时间" />
                </el-table>
            </div>
        </div>

        <!-- 上传合同文件弹窗 -->
        <el-dialog title="上传合同文件" :visible.sync="submitVisible" width="520px" append-to-body
            @closed="submitVisible = false">
            <el-form :model="submitForm" label-width="90px">
                <el-form-item label="方案说明" required>
                    <el-input v-model="submitForm.contractRemark" type="textarea" :rows="4"
                        placeholder="请填写方案说明 / 合同备注" />
                </el-form-item>
                <el-form-item label="合同文件" required>
                    <el-upload action="" :auto-upload="false" :limit="1" :file-list="uploadFileList"
                        :on-change="onContractFileChange" :on-remove="onContractFileRemove"
                        accept=".pdf,.doc,.doc,docx,.zip,.rar,.png,.jpg,.jpeg">
                        <el-button size="small" type="primary">点击选择文件</el-button>
                        <div slot="tip" class="el-upload__tip">
                            支持格式：.pdf .doc .docx .zip .rar .png .jpg .jpeg（单个文件，大小≤50MB）
                        </div>
                    </el-upload>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button @click="submitVisible = false">取消</el-button>
                <el-button type="primary" :loading="submitLoading" @click="submitPlan">提交</el-button>
            </div>
        </el-dialog>

        <!-- 打款下单弹窗（参考充值页：支付方式选择 + 微信/支付宝二维码 + socket 监听扫码） -->
        <el-dialog title="托管项目款" :visible.sync="payVisible" width="460px" append-to-body @closed="onPayClosed">
            <!-- 步骤一：选择支付方式 -->
            <div v-if="payStep === 'form' && payRow" class="pay-box">
                <div class="pay-row">
                    <span class="pay-label">托管金额：</span>
                    <span class="pay-amount">{{ formatBudget(payRow.amount) }}</span>
                </div>
                <div class="pay-row">
                    <span class="pay-label">支付方式：</span>
                    <el-radio-group v-model="payType">
                        <el-radio label="balancepay">余额支付</el-radio>
                        <el-radio label="wxpay">微信支付</el-radio>
                        <el-radio label="alipay">支付宝支付</el-radio>
                    </el-radio-group>
                </div>
                <div class="pay-tip">该款项平台已托管，确认后将发起支付</div>
            </div>
            <!-- 步骤二：扫码支付 -->
            <div v-else-if="payStep === 'qrcode'" class="pay-box">
                <div class="pay-qr-header">
                    <div class="pay-qr-title">{{ payType === 'wxpay' ? '微信支付' : '支付宝支付' }}</div>
                    <div class="pay-qr-desc">请使用{{ payType === 'wxpay' ? '微信' : '支付宝' }}扫描二维码支付</div>
                </div>
                <div class="pay-qr-amount">支付金额：{{ formatBudget(payRow && payRow.amount) }}</div>
                <div class="pay-qr-wrapper">
                    <div ref="payQrcode" class="pay-qr-code"></div>
                </div>
                <div class="pay-qr-tip">支付成功后系统会自动确认，请耐心等待…</div>
            </div>
            <div slot="footer">
                <el-button @click="payVisible = false">取消</el-button>
                <template v-if="payStep === 'form'">
                    <el-button type="primary" :loading="payLoading" @click="submitPay">确认下单</el-button>
                </template>
                <template v-else>
                    <el-button @click="backToPayForm">返回</el-button>
                </template>
            </div>
        </el-dialog>

        <div slot="footer">
            <el-button @click="visible = false">关闭</el-button>
        </div>
    </el-dialog>
</template>

<script>
import { softwareCustomDemandDetail, softwareCustomBidSelect, softwareCustomBidSubmit, softwareCustomBidCancelSelect, softwareCustomContractUpload, softwareCustomEscrowOrder } from '@/api/software'
import QRCode from 'qrcodejs2';
export default {
    name: 'CustomNeedDetail',
    props: {
        // 是否显示弹窗（v-model 风格：父组件用 .sync 控制）
        visible: {
            type: Boolean,
            default: false,
        },
        // 当前查看的定制需求详情对象（接口返回平铺 demand 对象）
        // 真实结构示例：{ id, demandNo, title, mode, crowdNumber, joinedCount, raisedAmount,
        // crowdStatus, budget, periodDays, budgetRemark, demand, demandFiles:[{url,name}], ... }
        // 若后端同时返回 bids / escrowRecords，则继续展示对应板块
        detail: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            localDetail: null, // 本地副本，用于刷新时直接更新而不影响父组件
            selectedBidId: null, // 已选标的投标记录ID（选标后才可提交方案）
            bidRefreshing: false, // 投标列表刷新中
            submitVisible: false, // 提交投标方案弹窗
            submitForm: {
                contractRemark: '', // 主合同备注 / 方案说明
                contractFile: '', // 主合同文件路径或附件ID（上传后由后端返回，前端不需手填）
            },
            uploadFileList: [], // 选中的合同文件（el-upload 受控列表）
            submitLoading: false,
            payLoading: false, // 打款按钮加载中
            payVisible: false, // 打款下单弹窗
            payRow: null, // 打款金额信息
            payType: 'balancepay', // 支付方式：balancepay/wxpay/alipay
            payStep: 'form', // 打款弹窗步骤：form=选支付方式 / qrcode=展示二维码
            payQrUrl: '', // 支付二维码内容（codeUrl）
            payWs: null, // 支付 WebSocket 连接
        }
    },
    computed: {
        // 实际使用的详情对象：本地副本优先，无副本时回退 props
        currentDetail() {
            return this.localDetail || this.detail
        },
        // 当前登录用户ID（用于判断是否需求方；项目统一走 vuex_userInfo）
        currentUserId() {
            try {
                const u = this.vuex_userInfo || (this.$store && this.$store.state && this.$store.state.vuex_userInfo)
                const id = u && (u.userId || u.id)
                return id != null && id !== '' ? Number(id) : null
            } catch (e) {
                return null
            }
        },
        // 当前需求状态（真实字段名为 demand.state；兼容旧 status 字段）
        demandStatus() {
            const d = this.d || {}
            const s = d.state !== undefined && d.state !== null && d.state !== '' ? d.state : d.status
            return s === '' || s === null || s === undefined ? null : Number(s)
        },
        // 当前状态文案（统一走 demandStatus，自动兼容 state/status）
        demandStatusText() {
            return this.statusText(this.demandStatus)
        },
        // 当前状态配色（统一走 demandStatus）
        demandStatusType() {
            return this.statusType(this.demandStatus)
        },
        // 是否为需求方（demand 中需求方ID，兼容多种字段名）
        isOwner() {
            const d = this.d || {}
            const ownerId = d.userId != null && d.userId !== '' ? d.userId :
                (d.createBy != null && d.createBy !== '' ? d.createBy :
                    (d.publisherId != null && d.publisherId !== '' ? d.publisherId :
                        (d.createUserId != null && d.createUserId !== '' ? d.createUserId : null)))
            return ownerId != null && ownerId !== '' && this.currentUserId != null && Number(ownerId) === this.currentUserId
        },
        // 是否可上传合同：已选标待签合同(status=2) + 尚未上传合同
        canUploadContract() {
            if (this.demandStatus !== 2) return false
            if (this.d.contractFile) return false
            return true
        },
        // 是否可撤销选标：仅需求方 + 已选标待签合同(status=2) + 尚未上传合同
        canCancelSelect() {
            if (!this.d) return false
            if (!this.isOwner) return false
            if (this.demandStatus !== 2) return false
            if (this.d.contractFile) return false
            return true
        },
        // 是否可打款：已签合同待托管项目款(status=3)
        canPay() {
            return this.demandStatus === 3
        },
        // 需求主信息：接口返回结构为 { demand:{...}, bids:[...], escrowRecords:[...] }
        // 直接用 detail.demand，不动后台返回数据，方便按接口结构定位问题
        d() {
            const detail = this.currentDetail || {}
            if (detail.demand && typeof detail.demand === 'object' && !Array.isArray(detail.demand)) {
                return detail.demand
            }
            return {}
        },
        isCrowdfunding() {
            return this.d.mode === 'crowdfunding'
        },
        // 投标方案列表：直接透传后台返回的数组，不做任何处理（列在模板里直接绑真实字段名）
        bidList() {
            const detail = this.currentDetail
            if (!detail) return []
            const arr = detail.bids || detail.bidList || detail.bidders
            return Array.isArray(arr) ? arr : []
        },
        // 是否已存在选中标（bids 中任意一条 selected 为 true）
        // 用于操作列：有选中标时，其余投标方不显示「选标」按钮
        hasSelectedBid() {
            return this.bidList.some(x => this.isBidSelected(x))
        },
        // 托管 / 追加资金记录（来自 detail.escrowRecords）
        escrowList() {
            const detail = this.currentDetail
            if (!detail) return []
            const list = detail.escrowRecords
            return Array.isArray(list) ? list : []
        },
    },
    watch: {
        // 打开弹窗时，同步本地副本并根据详情里的投标列表判断已选标记录
        detail: {
            immediate: true,
            handler(newVal) {
                this.localDetail = newVal
                const sel = this.bidList.find(x => this.isBidSelected(x))
                this.selectedBidId = sel ? this.getBidId(sel) : null
            },
        },
    },
    methods: {
        // ============ 选标 / 提交投标方案 / 刷新投标列表 ============
        // 取投标记录ID：优先 bid.id，回退 item.id / item.bidId（直接读后台返回的真实字段）
        getBidId(row) {
            if (!row || typeof row !== 'object') return null
            const bid = row.bid && typeof row.bid === 'object' ? row.bid : null
            if (bid && bid.id != null) return bid.id
            if (row.id != null) return row.id
            if (row.bidId != null) return row.bidId
            return null
        },
        // 判断某条投标是否已选标：只依据后台返回的 row.selected 字段
        isBidSelected(row) {
            if (!row || typeof row !== 'object') return false
            const s = row.selected
            return s === true || s === 1 || s === '1'
        },
        refreshBids(successMsg) {
            const id = this.d && (this.d.id !== undefined && this.d.id !== null && this.d.id !== '' ? this.d.id : this.d.customId)
            if (id === undefined || id === null || id === '') {
                this.$message.warning('未获取到需求ID，无法刷新')
                return
            }
            this.bidRefreshing = true
            softwareCustomDemandDetail(id)
                .then(res => {
                    const data = res && res.data !== undefined ? res.data : res
                    if (data && typeof data === 'object') {
                        // 保留本地已有的扩展字段，以详情接口返回优先覆盖
                        this.localDetail = { ...(this.localDetail || {}), ...data, id: data.id !== undefined ? data.id : id }
                        this.$message.success(successMsg || '刷新成功')
                    } else {
                        this.$message.warning('返回数据为空')
                    }
                })
                .catch(err => {
                    console.warn('刷新投标列表失败', err)
                    this.$message.error('刷新失败')
                })
                .finally(() => {
                    this.bidRefreshing = false
                })
        },
        selectBid(row) {
            const detail = this.currentDetail
            const customId = detail && (detail.id || detail.customId || (detail.demand && detail.demand.id))
            const bidId = this.getBidId(row)
            if (bidId === undefined || bidId === null || bidId === '') {
                this.$message.warning('未获取到投标记录ID')
                return
            }
            this.$confirm('确定选标该投标方吗？', '选标确认', {
                type: 'warning',
            }).then(() => {
                const payload = { bidId: Number(bidId) }
                if (customId !== undefined && customId !== null && customId !== '') payload.customId = Number(customId)
                softwareCustomBidSelect(payload)
                    .then(res => {
                        if (res && res.code === 200) {
                            this.$message.success('选标成功')
                            this.selectedBidId = bidId
                            // 选标成功：本地把当前这条 selected 置为 true、其余置 false，
                            // 使界面显示「撤标」且该条独占、其余按钮隐藏
                            if (this.localDetail && Array.isArray(this.localDetail.bids)) {
                                this.localDetail.bids.forEach(item => {
                                    if (item && typeof item === 'object') {
                                        const isThis = this.getBidId(item) === bidId
                                        item.selected = isThis
                                        if (item.bid && typeof item.bid === 'object') item.bid.selected = isThis
                                    }
                                })
                                this.localDetail = { ...this.localDetail }
                            }
                        } else {
                            this.$message.error((res && res.msg) || '选标失败')
                        }
                    })
                    .catch(err => {
                        console.warn('选标失败', err)
                        this.$message.error('选标失败')
                    })
            }).catch(() => { })
        },
        // 撤销选标（需求方撤销当前已选中的中标方，需求回到可投标状态）
        // 撤销后需重新拉详情，使 bids 里该条的 selected 复位、表格不再显示"已选标"
        cancelSelectBid(row) {
            const detail = this.currentDetail
            const customId = detail && (detail.id || detail.customId || (detail.demand && detail.demand.id))
            const bidId = this.getBidId(row)
            if (bidId === undefined || bidId === null || bidId === '') {
                this.$message.warning('未获取到投标记录ID')
                return
            }
            this.$confirm('确定撤销当前选标吗？撤销后该需求将回到可投标状态，若需改选其他投标方请重新选标。', '撤销选标确认', {
                type: 'warning',
                confirmButtonText: '确定撤销',
                cancelButtonText: '取消',
            }).then(() => {
                const payload = { bidId: Number(bidId) }
                if (customId !== undefined && customId !== null && customId !== '') payload.customId = Number(customId)
                softwareCustomBidCancelSelect(payload)
                    .then(res => {
                        if (res && res.code === 200) {
                            this.selectedBidId = null
                            // 撤标成功：本地立即把每条投标的 selected 复位为 false，
                            // 使界面立刻恢复为「全部可重新选标」，再拉详情与后台对齐
                            if (this.localDetail && Array.isArray(this.localDetail.bids)) {
                                this.localDetail.bids.forEach(item => {
                                    if (item && typeof item === 'object') {
                                        item.selected = false
                                        if (item.bid && typeof item.bid === 'object') item.bid.selected = false
                                    }
                                })
                                this.localDetail = { ...this.localDetail }
                            }
                            // 重新拉取详情，与后台 selected 状态对齐
                            this.refreshBids('撤标成功，需求已回到可投标状态')
                        } else {
                            this.$message.error((res && res.msg) || '撤标失败')
                        }
                    })
                    .catch(err => {
                        console.warn('撤标失败', err)
                        this.$message.error('撤标失败')
                    })
            }).catch(() => { })
        },
        // 选标后提交投标方案（需求方/中标方在选标后填写方案说明与附件）
        openSubmit() {
            if (this.selectedBidId == null) {
                this.$message.warning('请先选标再提交投标方案')
                return
            }
            this.submitForm = { contractRemark: '', contractFile: '' }
            this.uploadFileList = []
            this.submitVisible = true
        },
        submitPlan() {
            const detail = this.currentDetail
            const customId = detail && (detail.id || detail.customId || (detail.demand && detail.demand.id))
            if (customId === undefined || customId === null || customId === '') {
                this.$message.warning('未获取到需求ID')
                return
            }
            const remark = (this.submitForm.contractRemark || '').trim()
            if (!remark) {
                this.$message.warning('请填写方案说明')
                return
            }
            if (!this.uploadFileList || this.uploadFileList.length === 0) {
                this.$message.warning('请选择合同文件')
                return
            }
            const raw = this.uploadFileList[0] && this.uploadFileList[0].raw
            if (!raw) {
                this.$message.warning('文件读取失败，请重新选择')
                return
            }
            this.submitLoading = true
            // 第一步：调公共上传文件接口 /stage-api/commonFile/upLoadFile 拿到文件路径（contractFile）
            const params = `?fileType=contract&bizId=${customId}&number=contract`
            const fd = new FormData()
            fd.append('file', raw)
            this.$utils.upLoadFile(params, fd).then(res => {
                const path = res && res.data && res.data.urls
                if (!path) {
                    const msg = (res && res.header && res.header.msg) ? res.header.msg : '上传失败，请稍后重试'
                    throw { msg }
                }
                // 第二步：调业务接口登记合同（contractFile + contractRemark + 关联字段）
                const payload = { contractFile: path, contractRemark: remark }
                if (customId != null && customId !== '') payload.customId = Number(customId)
                if (this.selectedBidId != null && this.selectedBidId !== '') payload.bidId = Number(this.selectedBidId)
                return softwareCustomContractUpload(payload)
            }).then(res => {
                if (res && res.code === 200) {
                    this.$message.success('合同上传成功')
                    this.submitForm.contractFile = res.data && res.data.contractFile ? res.data.contractFile : ''
                    this.submitVisible = false
                    if (typeof this.refreshBids === 'function') this.refreshBids()
                } else {
                    this.$message.error((res && res.msg) || '合同提交失败')
                }
            }).catch(err => {
                this.$message.error((err && err.msg) ? err.msg : '合同上传失败')
            }).finally(() => {
                this.submitLoading = false
            })
        },
        onContractFileChange(file, fileList) {
            this.uploadFileList = fileList
            if (file && file.name) {
                this.$message.info(`已选择文件：${file.name}，请填写方案说明后点击「提交」上传`)
            }
        },
        onContractFileRemove(file, fileList) {
            this.uploadFileList = fileList
        },
        onClosed() {
            this.selectedBidId = null
            this.submitVisible = false
        },
        // 定制模式：personal-个人定制 / crowdfunding-多人众筹
        modeText(mode) {
            if (mode === 'crowdfunding') return '多人众筹'
            if (mode === 'personal') return '个人定制'
            return mode || '-'
        },
        // 需求状态：1-已发布待选标 2-已选标待签合同 3-已签合同待托管项目款 4-已托管项目款工作中
        // 5-已验收 6-交易完成 7-已暂停 8-已关闭 9-退款中 10-已退款 11-待审核 12-已审核待签约 13-已验收待打款
        statusText(status) {
            if (status === '' || status === null || status === undefined) return '-'
            const map = {
                1: '已发布待选标',
                2: '已选标待签合同',
                3: '已签合同待托管项目款',
                4: '已托管项目款工作中',
                5: '已交付待验收',
                6: '交易完成',
                7: '已暂停',
                8: '已关闭',
                9: '退款中',
                10: '已退款',
                11: '待审核',
                12: '已审核待签约',
                13: '已验收（待平台打款）',
            }
            return map[Number(status)] || String(status)
        },
        statusType(status) {
            const text = this.statusText(status)
            const map = {
                '已发布待选标': '',
                '已选标待签合同': 'warning',
                '已签合同待托管项目款': '',
                '已托管项目款工作中': '',
                '已交付待验收': 'danger',
                '交易完成': 'success',
                '已暂停': 'warning',
                '已关闭': 'info',
                '退款中': 'warning',
                '已退款': 'info',
                '待审核': 'info',
                '已审核待签约': 'warning',
                '已验收（待平台打款）': 'danger',
            }
            return map[text] || ''
        },
        // 需求方验收：调用 /stage-api/software/custom/acceptance（参数 customId）
        confirmAccept() {
            const customId = this.d && (this.d.id !== undefined && this.d.id !== null && this.d.id !== '' ? this.d.id : this.d.customId)
            if (customId === undefined || customId === null || customId === '') {
                this.$message.warning('未获取到定制需求ID')
                return
            }
            this.$confirm('确认验收该需求？验收后资金将进入结算流程。', '需求方验收', {
                confirmButtonText: '确认验收',
                cancelButtonText: '取消',
                type: 'warning',
            }).then(() => {
                return this.$http.post('/stage-api/software/custom/acceptance', { customId: Number(customId) })
            }).then(res => {
                if (res && res.code === 200) {
                    this.$successToast({ text: '验收成功', sub: '需求已验收，资金进入结算流程', duration: 2300 })
                    this.refreshBids('验收成功')
                } else {
                    this.$message.error((res && res.msg) || '验收失败，请稍后重试')
                }
            }).catch(err => {
                // 用户取消 $confirm 不提示；接口错误给出提示
                if (err === 'cancel' || (err && err.toString && err.toString().indexOf('cancel') > -1)) return
                const msg = (err && err.msg) ? err.msg : '验收失败，请稍后重试'
                this.$message.error(msg)
            })
        },
        // 打款按钮：打开托管项目款下单弹窗（参考 dealer.vue 购买弹窗）
        openPay() {
            // 托管金额 = 当前选中标方案的报价：bids[].bid.price
            const selectedBid = this.bidList.find(x => this.isBidSelected(x))
            const bidPrice = selectedBid && selectedBid.bid && selectedBid.bid.price != null
                ? Number(selectedBid.bid.price) : null
            if (!bidPrice || isNaN(bidPrice) || bidPrice < 1) {
                this.$message.warning('无法获取托管金额，请确认已选中标方案的报价(bids.bid.price)')
                return
            }
            this.payRow = { amount: bidPrice }
            this.payType = 'balancepay'
            this.payVisible = true
        },
        // 提交托管项目款订单（参考充值页：下单成功后展示二维码 + socket 监听）
        submitPay() {
            const detail = this.currentDetail
            const customId = detail && (detail.id || detail.customId || (detail.demand && detail.demand.id))
            if (customId === undefined || customId === null || customId === '') {
                this.$message.warning('未获取到需求ID')
                return
            }
            if (!this.payRow) return
            const amount = this.payRow.amount
            // 取当前已选中的投标方案ID
            const selectedBid = this.bidList.find(x => this.isBidSelected(x))
            const bidId = selectedBid ? this.getBidId(selectedBid) : null
            this.payLoading = true
            const payload = {
                customId: Number(customId),
                amount,
                payType: this.payType,
                projectType: 1, // 首次托管
            }
            if (bidId != null && bidId !== '') payload.bidId = Number(bidId)
            softwareCustomEscrowOrder(payload)
                .then(res => {
                    if (res && res.code === 200) {
                        const data = (res && res.data) || {}
                        // 余额支付：直接成功；微信/支付宝：展示二维码并监听 socket
                        if (this.payType === 'balancepay') {
                            this.$message.success('托管下单成功')
                            this.payVisible = false
                            this.refreshBids()
                            return
                        }
                        const codeUrl = data.codeUrl || data.payUrl || ''
                        if (!codeUrl) {
                            this.$message.error('未获取到支付二维码')
                            return
                        }
                        this.payQrUrl = codeUrl
                        this.payStep = 'qrcode'
                        this.$nextTick(() => {
                            this.renderPayQr()
                            this.initPayWs()
                        })
                    } else {
                        this.$message.error((res && res.msg) || '托管下单失败')
                    }
                })
                .catch(err => {
                    console.warn('托管下单失败', err)
                    this.$message.error((err && err.msg) || '托管下单失败')
                })
                .finally(() => {
                    this.payLoading = false
                })
        },
        // 渲染支付二维码（参考充值页 QRCode 用法）
        renderPayQr() {
            const container = this.$refs.payQrcode
            if (!container) return
            while (container.firstChild) {
                container.removeChild(container.firstChild)
            }
            /* eslint-disable no-new */
            new QRCode(container, {
                text: this.payQrUrl,
                width: 200,
                height: 200,
            })
        },
        // 建立支付监听 WebSocket（参考充值页：/stage-api/pay-websocket/{token}?userId=...&businessType=pay）
        initPayWs() {
            // 先清理可能存在的旧连接
            this.closePayWs()
            const token = this.vuex_token || (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.token)
            const userId = this.currentUserId
            if (!token || userId == null) {
                console.warn('缺少 WebSocket 连接参数 token/userId')
                return
            }
            const params = `/${token}?userId=${userId}&businessType=pay`
            const wsUrl = (process.env.VUE_APP_BASE_WS || '') + '/stage-api/pay-websocket' + params
            try {
                this.payWs = new WebSocket(wsUrl)
                const that = this
                this.payWs.onopen = function () {
                    console.log('支付 WebSocket 已连接')
                }
                this.payWs.onmessage = function (event) {
                    console.log('支付 WebSocket 消息', event.data)
                    try {
                        const { code, msg } = JSON.parse(event.data)
                        if (code == 200) {
                            that.payVisible = false
                        }
                        if (msg === '支付成功') {
                            that.$alert('支付成功，托管款项已确认', '提示', {
                                confirmButtonText: '确定',
                            })
                            that.payVisible = false
                            that.refreshBids()
                        }
                    } catch (e) {
                        console.warn('解析 WebSocket 消息失败', e)
                    }
                }
                this.payWs.onerror = function (err) {
                    console.warn('支付 WebSocket 错误', err)
                }
            } catch (e) {
                console.warn('创建支付 WebSocket 失败', e)
            }
        },
        // 关闭支付 WebSocket
        closePayWs() {
            if (this.payWs) {
                try {
                    this.payWs.close()
                } catch (e) { }
                this.payWs = null
            }
        },
        // 返回支付方式选择步骤
        backToPayForm() {
            this.closePayWs()
            this.payStep = 'form'
            this.payQrUrl = ''
        },
        onPayClosed() {
            this.closePayWs()
            this.payRow = null
            this.payStep = 'form'
            this.payQrUrl = ''
            this.payLoading = false
        },
        // 众筹状态（按后端实际语义调整）
        crowdStatusText(s) {
            if (s === 0 || s === '0') return '筹集中'
            if (s === 1 || s === '1') return '众筹成功'
            if (s === 2 || s === '2') return '众筹失败'
            return s != null && s !== '' ? String(s) : '-'
        },
        // 托管状态（按后端实际语义调整）
        escrowStatusText(s) {
            if (s === 0 || s === '0') return '未托管'
            if (s === 1 || s === '1') return '已托管'
            return s != null && s !== '' ? String(s) : '-'
        },
        // 托管记录资金类型（projectType：1-首次托管 2-追加托管）
        escrowProjectTypeText(t) {
            if (t === 1 || t === '1') return '首次托管'
            if (t === 2 || t === '2') return '追加托管'
            return t != null && t !== '' ? String(t) : '-'
        },
        // 托管记录支付状态（status：0-待支付 1-已支付 2-已退款）
        escrowPayStatusText(s) {
            if (s === 0 || s === '0') return '待支付'
            if (s === 1 || s === '1') return '已支付'
            if (s === 2 || s === '2') return '已退款'
            return s != null && s !== '' ? String(s) : '-'
        },
        formatBudget(b) {
            if (b === '' || b === null || b === undefined) return '-'
            if (typeof b === 'object') return '-'
            const n = Number(b)
            return isNaN(n) ? String(b) : n + ' 元'
        },
        fileList(demandFile) {
            if (Array.isArray(demandFile)) return demandFile.filter(f => f && (typeof f === 'object' ? (f.url || f.name) : String(f).trim()))
            if (typeof demandFile === 'string' && demandFile.trim()) return demandFile.split(',').filter(f => f && f.trim())
            if (demandFile && typeof demandFile === 'object') return [demandFile]
            return []
        },
        fileName(path) {
            if (path === null || path === undefined || path === '') return ''
            if (typeof path === 'object') {
                const p = path.name || path.url || path.fileName || path.path || ''
                return this.fileName(p)
            }
            const str = String(path)
            const idx = str.lastIndexOf('/')
            return idx >= 0 ? str.substring(idx + 1) : str
        },
        fileUrl(path) {
            if (path === null || path === undefined || path === '') return 'javascript:void(0)'
            let p = path
            if (typeof path === 'object') {
                p = path.url || path.name || path.fileName || path.path || ''
            }
            if (!p) return 'javascript:void(0)'
            return process.env.VUE_APP_BASE_API ? process.env.VUE_APP_BASE_API + p : p
        },
    },
}
</script>

<style lang="scss" scoped>
.bid-section {
    margin-top: 20px;

    .bid-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 10px;
        color: #333;
    }

    .file-item {
        line-height: 1.8;
    }

    .file-link {
        color: #409eff;
        text-decoration: underline;
        word-break: break-all;
    }

    .bid-submit-bar {
        margin-top: 12px;
        display: flex;
        align-items: center;
        gap: 10px;

        .bid-submit-tip {
            font-size: 13px;
            color: #67c23a;
        }
    }
}

.escrow-section {
    margin-top: 20px;

    .escrow-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 10px;
        color: #333;
    }
}
</style>

<style lang="scss">
/* 打款下单弹窗：append-to-body 渲染在 body 下，必须用全局样式 */
.pay-box {
    .pay-row {
        display: flex;
        align-items: center;
        margin-bottom: 14px;

        .pay-label {
            width: 80px;
            color: #666;
        }

        .pay-amount {
            font-size: 16px;
            font-weight: bold;
            color: #e6a23c;
        }
    }

    .pay-tip {
        margin-top: 4px;
        font-size: 12px;
        color: #909399;
    }

    .pay-qr-header {
        text-align: center;
        margin-bottom: 12px;

        .pay-qr-title {
            font-size: 18px;
            font-weight: 600;
            color: #303133;
        }

        .pay-qr-desc {
            font-size: 13px;
            color: #909399;
            margin-top: 4px;
        }
    }

    .pay-qr-amount {
        text-align: center;
        font-size: 22px;
        font-weight: 700;
        color: #e6a23c;
        margin-bottom: 14px;
    }

    .pay-qr-wrapper {
        display: flex;
        justify-content: center;
        background: #fff;
        padding: 16px;
        border-radius: 12px;
        border: 1px solid #ebeef5;
        margin-bottom: 12px;

        .pay-qr-code {
            width: 200px;
            height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .pay-qr-tip {
        text-align: center;
        font-size: 12px;
        color: #909399;
    }
}
</style>
